const sectionsToRender = [
  {
    id: '#cart-counter',
    section: 'cart-counter',
    selector: '#shopify-section-cart-counter'
  },
  {
    id: '#CartDrawer-Body',
    section: 'cart-drawer',
    selector: '#shopify-section-cart-drawer #CartDrawer-Body'
  }
];

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.recommendationsPerformed = false;

    this.addEventListener(
      'keyup',
      event => event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setCartLink();
  }

  connectedCallback() {
    this.toggleCheckoutButton();
  }

  performRecommendations() {
    const recommendationsHolder = this.querySelector(
      '[data-cart-drawer-upsells]'
    );
    if (!recommendationsHolder || this.recommendationsPerformed)
      return;
    this.recommendationsPerformed = true;

    if (!recommendationsHolder.dataset.url) return;

    fetch(recommendationsHolder.dataset.url)
      .then(response => response.text())
      .then(text => {
        const fresh = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('[data-cart-drawer-upsells]');
        if (fresh) recommendationsHolder.outerHTML = fresh.outerHTML;
      })
      .catch(error => console.error('Cart drawer upsells failed', error));
  }

  setCartLink() {
    const cartLinks = document.querySelectorAll('[data-cart-link]');
    cartLinks.forEach(cartLink => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', event => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', event => {
        if (event.code.toUpperCase() !== 'SPACE') return;
        event.preventDefault();
        this.open(cartLink);
      });
    });
  }

  onCartAdd(event) {
    event.preventDefault();

    addToCart(
      event.target,
      this.getSectionsToRender().map(section => section.section)
    )
      .then(response => {
        if (response.status) throw new Error(response.message);

        this.renderContents(response);
      })
      .catch(error => {
        console.error(error);
      });
  }

  scrollLockTarget() {
    return this.querySelector('.cart-drawer__inner')
      || this.querySelector('#CartDrawer-Body');
  }

  // Root-relative section-render URL for the cart drawer, so it works from any
  // page -- including app-proxy pages like /a/bundles/... where a page-relative
  // ?section_id= request would not resolve.
  drawerSectionUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root = root + '/';
    return root + '?section_id=cart-drawer';
  }

  /*
   * Re-fetch the cart drawer's server-rendered HTML and apply it. This is the
   * safety net that guarantees the drawer shows the correct items + summary +
   * checkout + upsell no matter HOW the items got into the cart (theme add,
   * our upsell, or a third-party bundle/app that adds items its own way and
   * doesn't refresh our drawer). Silent no-op on any failure.
   */
  refresh() {
    if (typeof window.arApplyCartSections !== 'function') return Promise.resolve();
    return fetch(this.drawerSectionUrl(), { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return;
        var trimmed = html.replace(/^\\s+/, '');
        if (trimmed.charAt(0) === '{') return; // not section HTML
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.querySelector('#CartDrawer-Body')) return;
        var isEmpty = !doc.querySelector('cart-drawer-items');
        window.arApplyCartSections({ 'cart-drawer': html }, { surface: 'drawer', empty: isEmpty });
      })
      .catch(function () {});
  }

  open(opener) {
    if (opener) this.setActiveElement(opener);
    this.classList.add('is-visible');
    this.addEventListener(
      'transitionend',
      () => {
        this.focusOnCartDrawer();
      },
      { once: true }
    );
    bodyScroll.lock(this.scrollLockTarget());
    // Always reconcile the drawer with the real cart on open (covers bundle
    // apps / any add path that didn't refresh our drawer).
    if (!this._skipRefreshOnce) {
      this.refresh().then(() => this.performRecommendations());
    } else {
      this._skipRefreshOnce = false;
      this.performRecommendations();
    }
  }

  close() {
    this.classList.remove('is-visible');
    removeTrapFocus(this.activeElement);
    bodyScroll.unlock(this.scrollLockTarget());
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  focusOnCartDrawer() {
    const containerToTrapFocusOn = this.querySelector('#CartDrawer');
    const focusElement = this.querySelector('[data-drawer-close]');
    trapFocus(containerToTrapFocusOn, focusElement);
  }

  renderContents(response) {
    // Apply the response we already have (fast, no extra fetch), then open --
    // and skip the open() refetch since we just rendered fresh contents.
    if (response && response.sections && typeof window.arApplyCartSections === 'function') {
      window.arApplyCartSections(response.sections, { surface: 'drawer', empty: false });
      this._skipRefreshOnce = true;
    }
    this.recommendationsPerformed = false;
    this.open();
  }

  getSectionsToRender() {
    return sectionsToRender;
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  toggleCheckoutButton() {
    this.addEventListener('change', event => {
      if (event.target.getAttribute('name') !== 'terms') return;

      this.querySelector('[name="checkout"]')?.classList.toggle(
        'not-allowed',
        !event.target.checked
      );
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  constructor() {
    super();
    this.cartDrawer = document.querySelector('cart-drawer');
  }

  performRecommendations() {
    if (this.cartDrawer) {
      this.cartDrawer.recommendationsPerformed = false;
      this.cartDrawer.performRecommendations();
    }
  }

  getSectionsToRender() {
    return sectionsToRender;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);